import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  RefreshCw, Loader2, AlertCircle, CheckCircle, Search, Wallet,
  Clock, AlertTriangle, XCircle, ServerCog,
} from 'lucide-react';
import api from '../../services/api';

const STATUS_STYLES = {
  active: { color: '#00FF88', label: 'Active', Icon: CheckCircle },
  expired: { color: '#FF6B35', label: 'Expirée', Icon: XCircle },
  disabled: { color: '#FF4D4D', label: 'Désactivée', Icon: AlertTriangle },
  waiting: { color: '#FFC107', label: 'En attente', Icon: Clock },
};

const ISSUE_STYLES = {
  missing_on_goldenott: { color: '#FF4D4D', label: 'Absente de GoldenOTT' },
  line_disabled: { color: '#FF4D4D', label: 'Ligne désactivée' },
  line_expired: { color: '#FF6B35', label: 'Ligne expirée' },
  expiry_drift: { color: '#FFC107', label: "Dérive d'expiration" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || { color: '#B8B8BA', label: status, Icon: AlertCircle };
  const Icon = s.Icon;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ color: s.color, backgroundColor: `${s.color}1A` }} data-testid="line-status-badge">
      <Icon className="h-3.5 w-3.5" />{s.label}
    </span>
  );
};

const GoldenOTTAudit = () => {
  const [credits, setCredits] = useState(null);
  const [report, setReport] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [error, setError] = useState('');

  const [lineId, setLineId] = useState('');
  const [lineStatus, setLineStatus] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [lineError, setLineError] = useState('');

  useEffect(() => { loadCredits(); }, []);

  const loadCredits = async () => {
    try {
      const res = await api.get('/admin/goldenott/credits');
      setCredits(res.data);
    } catch (e) {
      setCredits({ configured: false });
    }
  };

  const runReconcile = async () => {
    setReconciling(true); setError(''); setReport(null);
    try {
      const res = await api.get('/admin/goldenott/reconcile');
      setReport(res.data);
      loadCredits();
    } catch (e) {
      setError(e.response?.data?.detail || 'Échec de la réconciliation GoldenOTT');
    } finally { setReconciling(false); }
  };

  const lookupLine = async () => {
    const id = lineId.trim();
    if (!id) return;
    setLookingUp(true); setLineError(''); setLineStatus(null);
    try {
      const res = await api.get(`/admin/goldenott/line/${encodeURIComponent(id)}`);
      setLineStatus(res.data);
    } catch (e) {
      setLineError(e.response?.data?.detail || 'Ligne introuvable');
    } finally { setLookingUp(false); }
  };

  return (
    <div className="space-y-6" data-testid="goldenott-audit">
      {/* En-tête + crédits */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ServerCog className="h-7 w-7 text-[#FF6B35]" />
            <div>
              <h3 className="text-white text-xl font-bold">Audit & Réconciliation GoldenOTT</h3>
              <p className="text-[#6B6B6E] text-sm">Compare les accès locaux avec le compte GoldenOTT et vérifie l'état des lignes.</p>
            </div>
          </div>
          {credits && credits.configured && (
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${credits.low ? 'bg-[#FF6B35]/15' : 'bg-[#00FF88]/10'}`}
              data-testid="goldenott-credit-badge">
              <Wallet className="h-5 w-5" style={{ color: credits.low ? '#FF6B35' : '#00FF88' }} />
              <div className="leading-tight">
                <div className="text-white font-bold">{credits.credit} crédits</div>
                <div className="text-xs" style={{ color: credits.low ? '#FF6B35' : '#6B6B6E' }}>
                  {credits.low ? 'Solde bas' : 'Solde OK'} · {credits.pending_count} en attente
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Recherche statut ligne */}
      <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Search className="h-4 w-4 text-[#FF6B35]" /> Statut d'une ligne
        </h4>
        <div className="flex flex-wrap gap-2">
          <input value={lineId} onChange={(e) => setLineId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && lookupLine()}
            placeholder="ID de ligne GoldenOTT"
            className="flex-1 min-w-[200px] bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-[#FF6B35]"
            data-testid="line-id-input" />
          <button onClick={lookupLine} disabled={lookingUp || !lineId.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B35] text-black font-semibold text-sm disabled:opacity-50"
            data-testid="line-lookup-btn">
            {lookingUp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Vérifier
          </button>
        </div>
        {lineError && <p className="text-[#FF4D4D] text-sm mt-3 flex items-center gap-2"><AlertCircle className="h-4 w-4" />{lineError}</p>}
        {lineStatus && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="line-status-result">
            <div className="col-span-2 sm:col-span-3 flex items-center justify-between">
              <span className="text-white font-mono text-sm">{lineStatus.username} · #{lineStatus.line_id}</span>
              <StatusBadge status={lineStatus.status} />
            </div>
            <div className="bg-[#222226] rounded-lg p-3"><div className="text-[#6B6B6E] text-xs">Écrans</div><div className="text-white font-semibold">{lineStatus.max_connections ?? '—'}</div></div>
            <div className="bg-[#222226] rounded-lg p-3"><div className="text-[#6B6B6E] text-xs">Expiration</div><div className="text-white font-semibold">{lineStatus.exp_date || '—'}</div></div>
            <div className="bg-[#222226] rounded-lg p-3"><div className="text-[#6B6B6E] text-xs">Jours restants</div><div className="text-white font-semibold">{lineStatus.days_remaining ?? '—'}</div></div>
          </div>
        )}
      </div>

      {/* Réconciliation */}
      <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-white font-semibold flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-[#FF6B35]" /> Réconciliation
          </h4>
          <button onClick={runReconcile} disabled={reconciling}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B35] text-black font-semibold text-sm disabled:opacity-50"
            data-testid="run-reconcile-btn">
            {reconciling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {reconciling ? 'Analyse…' : 'Lancer la réconciliation'}
          </button>
        </div>

        {error && <p className="text-[#FF4D4D] text-sm flex items-center gap-2"><AlertCircle className="h-4 w-4" />{error}</p>}

        {report && (
          <div data-testid="reconcile-report">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {[
                { label: 'Lignes GoldenOTT', value: report.remote_lines },
                { label: 'Accès locaux', value: report.local_lines },
                { label: 'Anomalies', value: report.issue_count, danger: report.issue_count > 0 },
                { label: 'Orphelines distantes', value: report.orphan_remote_count },
              ].map((c) => (
                <div key={c.label} className="bg-[#222226] rounded-lg p-4">
                  <div className="text-[#6B6B6E] text-xs">{c.label}</div>
                  <div className="text-2xl font-bold" style={{ color: c.danger ? '#FF6B35' : '#fff' }}>{c.value}</div>
                </div>
              ))}
            </div>

            {report.issue_count === 0 ? (
              <p className="text-[#00FF88] text-sm flex items-center gap-2" data-testid="reconcile-no-issues">
                <CheckCircle className="h-4 w-4" /> Aucune anomalie détectée sur les accès gérés.
              </p>
            ) : (
              <div className="space-y-2" data-testid="reconcile-issues">
                {report.issues.map((iss, i) => {
                  const st = ISSUE_STYLES[iss.type] || { color: '#FFC107', label: iss.type };
                  return (
                    <div key={i} className="flex flex-wrap items-center justify-between gap-2 bg-[#222226] rounded-lg p-3 border-l-2"
                      style={{ borderColor: st.color }}>
                      <div>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full mr-2"
                          style={{ color: st.color, backgroundColor: `${st.color}1A` }}>{st.label}</span>
                        <span className="text-white text-sm">{iss.user_email || '—'}</span>
                      </div>
                      <div className="text-[#6B6B6E] text-xs">
                        ligne #{iss.line_id}{iss.goldenott_exp ? ` · GoldenOTT: ${iss.goldenott_exp}` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GoldenOTTAudit;
